import React from 'react';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {useLocaleKeys} from '../../../../locale-keys/LocaleKeys';
import {LineItemModel} from '../../../../domain/models/checkout/LineItems/LineItem.model';
import {classes} from './LineItem.st.css';
import {getLineItemViolationIfItExists} from './LineItem.utils';
import {LineItemAdditionalInfo} from './LineItemAdditionalInfo';
import {useControllerProps} from '../../Widget/ControllerContext';
import {ItemLayout} from '../../../../common/components/Item/ItemLayout';
import {ItemImage, Thumbnail} from '../../../../common/components/Item/ItemImage/ItemImage';
import {ItemName} from '../../../../common/components/Item/ItemName/ItemName';
import {ItemPrice} from '../../../../common/components/Item/ItemPrice/ItemPrice';
import {ItemDetails} from '../../../../common/components/Item/ItemDetails/ItemDetails';
import {ItemSubscription} from './ItemSubscription/ItemSubscription';
import {NUMBER_OF_OPTIONS_TO_SHOW_BEFORE_COLLAPSE, SPECS} from '../../../../common/constants';
import {Text, TextPriority} from '@wix/wix-ui-tpa/cssVars';
import {getLineItemSubscriptionFrequencyText} from '../../../../domain/utils/getLineItemSubscriptionFrequencyText';
import {mapLineItemToDetails} from './mapLineItemToDetails';

export enum LineItemDataHooks {
  LineItemWrapper = 'LineItemDataHooks.LineItemWrapper',
  Name = 'LineItemDataHooks.Name',
  ComparePrice = 'LineItemDataHooks.ComparePrice',
  Price = 'LineItemDataHooks.Price',
  SubscriptionFrequency = 'LineItemDataHooks.SubscriptionFrequency',
  Subscription = 'LineItemDataHooks.Subscription',
  Options = 'LineItemDataHooks.Options',
  OutOfStock = 'LineItemDataHooks.OutOfStock',
  Image = 'LineItemDataHooks.Image',
  DefaultImage = 'LineItemDataHooks.DefaultImage',
  QuantityBadge = 'LineItemDataHooks.QuantityBadge',
}

export const LineItemRow = ({
  lineItem,
  useLayoutForLongPrice,
}: {
  lineItem: LineItemModel;
  useLayoutForLongPrice: boolean;
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const {
    checkoutStore: {shouldShowViolations, cartSummary},
  } = useControllerProps();
  const localeKeys = useLocaleKeys();
  const {experiments} = useExperiments();
  const checkoutUIRevampOrderSummary = experiments.enabled(SPECS.CheckoutUIRevampOrderSummary);

  const hidePrice = lineItem.pricing.priceUndetermined;
  const shouldDisplayFrequency =
    lineItem.attributes.subscriptionInfo &&
    lineItem.attributes.subscriptionInfo.subscriptionSettings.billingCycles !== 1;

  const thumbnail: Thumbnail | undefined = lineItem.attributes.image
    ? {
        src: lineItem.attributes.image.src,
        height: lineItem.attributes.image.height,
        width: lineItem.attributes.image.width,
        altText: lineItem.attributes.image.altText,
      }
    : undefined;

  const getSalePriceAriaLabel = () => {
    return lineItem.pricing.breakdown.comparePrice
      ? localeKeys.checkout.orderSummary.salePrice.ariaLabel({
          price: lineItem.pricing.totalPrice.formattedAmount,
        })
      : localeKeys.checkout.orderSummary.price.ariaLabel({
          price: lineItem.pricing.totalPrice.formattedAmount,
        });
  };

  const isItemOutOfStock = lineItem.quantityInfo.quantity === 0;
  const lineItemViolation = shouldShowViolations
    ? getLineItemViolationIfItExists(cartSummary!.violations, lineItem.id)
    : undefined;

  const getComparePriceValueText = () => {
    return lineItem.pricing.breakdown.comparePriceByQuantity?.formattedAmount;
  };

  const hasPriceOnShowMore = lineItem.pricing.breakdown.totalModifiersPrice?.amount !== 0;
  const priceOnShowMore = hasPriceOnShowMore
    ? lineItem.pricing.breakdown.totalModifiersPrice?.formattedAmount
    : undefined;

  const additionalInfoExistClassName = checkoutUIRevampOrderSummary
    ? classes.additionalInfoExist
    : classes.additionalInfoExistOld;
  return (
    <li
      data-hook={LineItemDataHooks.LineItemWrapper}
      className={`${isItemOutOfStock || lineItemViolation ? additionalInfoExistClassName : ''}`}>
      <ItemLayout
        descriptionWithColumns={!useLayoutForLongPrice}
        shouldDisplayFrequency={shouldDisplayFrequency}
        Icon={
          <ItemImage
            thumbnail={thumbnail}
            imageDataHook={LineItemDataHooks.Image}
            defaultImageDataHook={LineItemDataHooks.DefaultImage}
            quantity={lineItem.quantityInfo.fixedQuantity ? undefined : lineItem.quantityInfo.quantity}
            quantityBadgeDataHook={LineItemDataHooks.QuantityBadge}
          />
        }
        MainDescription={<ItemName name={lineItem.name} dataHook={LineItemDataHooks.Name} />}
        SecondaryDescription={
          hidePrice ? null : (
            <div className={classes.priceContainer}>
              <ItemPrice
                comparePriceDataHook={LineItemDataHooks.ComparePrice}
                priceDataHook={LineItemDataHooks.Price}
                shortPrice={!useLayoutForLongPrice}
                formattedPrice={lineItem.pricing.totalPrice.formattedAmount}
                priceAriaLabel={getSalePriceAriaLabel()}
                formattedComparePrice={getComparePriceValueText()}
                comparePriceAriaLabel={
                  lineItem.pricing.breakdown.comparePrice
                    ? localeKeys.checkout.orderSummary.regularPrice.ariaLabel({
                        price: getComparePriceValueText(),
                      })
                    : undefined
                }
              />
              {shouldDisplayFrequency && (
                <Text
                  className={classes.subscriptionFrequencyText}
                  priority={TextPriority.secondary}
                  data-hook={LineItemDataHooks.SubscriptionFrequency}>
                  {getLineItemSubscriptionFrequencyText(
                    lineItem.attributes.subscriptionInfo!.subscriptionSettings,
                    localeKeys
                  )}
                </Text>
              )}
            </div>
          )
        }
        Details={
          <div className={classes.itemData}>
            {lineItem.attributes.subscriptionInfo && (
              <ItemSubscription
                subscription={lineItem.attributes.subscriptionInfo}
                dataHook={LineItemDataHooks.Subscription}
              />
            )}
            <ItemDetails
              dataHook={LineItemDataHooks.Options}
              itemId={lineItem.id}
              details={mapLineItemToDetails(lineItem, localeKeys, checkoutUIRevampOrderSummary)}
              unitBasePrice={hasPriceOnShowMore ? lineItem.pricing.breakdown.basePrice?.formattedAmount : undefined}
              collapseSettings={{
                numberOfOptionsToCollapseAfter: NUMBER_OF_OPTIONS_TO_SHOW_BEFORE_COLLAPSE,
                showMoreTitle: localeKeys.checkout.order_summary.items.showMore({
                  modifierTotal: priceOnShowMore,
                }),
                showLessTitle: localeKeys.checkout.order_summary.items.showLess(),
              }}
            />
          </div>
        }
        AdditionalInfo={
          (isItemOutOfStock || lineItemViolation) && (
            <LineItemAdditionalInfo
              lineItem={lineItem}
              isItemOutOfStock={isItemOutOfStock}
              lineItemViolation={lineItemViolation}
            />
          )
        }
      />
    </li>
  );
};
